import * as React from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import FullCalendar from "@fullcalendar/react"; // the React component
import { EventApi, EventClickArg } from "@fullcalendar/core"; // the types
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import axios from "axios";
import { DateTime } from "luxon";
import RoomScheduleFullCalendar, { roomColors, convertScheduleToEvents } from "./RoomScheduleFullCalendar";
import "./mobile.css";
import type { DateClickArg, EventResizeDoneArg } from "@fullcalendar/interaction";
import { Dialog, Button, Flex, Text, List, FormField, Form, Dropdown, Input, Checkbox } from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import { toast } from "react-toastify";
import type {
  EventDropArg
} from "@fullcalendar/core";

export default function RoomScheduleMobile() {
  const calendarRef = useRef<FullCalendar>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [schedule, setSchedule] = useState<any[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventApi | null>(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [openBookingForm, setOpenBookingForm] = useState(false);
  const [allDay, setAllDay] = useState(false);
  const [directEvent, setDirectEvent] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [attendees, setAttendees] = useState<string[]>([]);
  const [isRefetch, setIsRefetch] = useState<number>(0);
  const [allRooms, setAllRooms] = useState<string[]>([]);
  const [recurrence, setRecurrence] = useState<{
    frequency: "daily" | "weekly" | "monthly";
    interval: number;
    daysOfWeek: string[]; // ["monday","wednesday",...]
    endDate: string;
  }>({
    frequency: "weekly",
    interval: 1,
    daysOfWeek: [],
    endDate: DateTime.now().toISODate()
  });
  const [showMyEvents, setShowMyEvents] = useState(false);

  const [formData, setFormData] = useState<{ start: Date; end: Date; subject: string }>({
    start: new Date(),
    end: new Date(),
    subject: ""
  });
  const defaultRecurrence = {
    frequency: "weekly" as "daily" | "weekly" | "monthly",
    interval: 1,
    daysOfWeek: [] as string[],
    // khởi tạo endDate dựa trên formData.start
    endDate: DateTime.fromJSDate(formData.start).toISODate()
  };

  const [bookingRoom, setBookingRoom] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [userSuggestions, setUserSuggestions] = useState<{ displayName: string; mail: string }[]>([]);
  // 1) Ref cho input date picker
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [roomQuery, setRoomQuery] = useState<string>("");
  const [roomSuggestions, setRoomSuggestions] = useState<string[]>([]);
  // RoomScheduleMobile.tsx (sau các useState hiện tại) :contentReference[oaicite:0]{index=0}:contentReference[oaicite:1]{index=1}
  const [showDeleteConfirmMobile, setShowDeleteConfirmMobile] = useState(false);
  const [pendingDeleteSeries, setPendingDeleteSeries] = useState<boolean | null>(null);
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await axios.get("/api/rooms");
        const emails = res.data.map((r: any) => r.emailAddress);
        setAllRooms(emails);
        setSelectedRooms(emails); // ← pre-select tất cả
      } catch (err) {
        console.error("Error fetching rooms:", err);
      }
    };
    fetchRooms();
  }, []);

  const handleDeleteMobileEvent = async (deleteSeries: boolean) => {
    setShowDeleteConfirmMobile(false);
    if (!selectedEvent) return;

    // capture trước để không phụ thuộc vào state sau này
    const { id, extendedProps: { seriesMasterId } } = selectedEvent;

    try {
      // 2) Gửi request xóa
      await axios.delete(
        `/api/meeting-room-booking/${selectedEvent.id}`,
        { data: { organizerEmail: userEmail, deleteSeries, seriesMasterId: seriesMasterId ?? null } }
      );

      // 3) Đóng detail modal và reset selectedEvent
      setOpenDetails(false);
      setSelectedEvent(null);

      // 4) Cập nhật lại schedule state
      setSchedule(prev => {
        if (deleteSeries && seriesMasterId) {
          // xóa toàn bộ sự kiện có cùng seriesMasterId
          return prev.filter(ev => ev.seriesMasterId !== seriesMasterId);
        } else {
          // chỉ xóa occurrence hiện tại
          return prev.filter(ev => ev.id !== id);
        }
      });

      // 5) (Optionally) nếu bạn không dùng schedule state để render, có thể gọi:
      // calendarRef.current?.getApi().refetchEvents();

      toast.success("Xóa thành công");
    } catch (err) {
      console.error(err);
      toast.error("Xóa thất bại");
    }
  };
  const handleEditOccurrence = () => {
    if (!selectedEvent) return;
    // Prefill giống trước
    setFormData({
      start: selectedEvent.start!,
      end: selectedEvent.end || selectedEvent.start!,
      subject: selectedEvent.title
    });
    setBookingRoom(selectedEvent.extendedProps.room);
    setAttendees(selectedEvent.extendedProps.participants || []);
    setAllDay(selectedEvent.allDay || false);
    // Xem như không còn là recurrence
    setIsRecurring(false);
    setRecurrence(defaultRecurrence);
    setOpenDetails(false);
    setOpenBookingForm(true);
  };

  const handleEditSeries = async () => {
    if (!selectedEvent) return;
    const masterId = selectedEvent.extendedProps.seriesMasterId;
    if (!masterId) {
      toast.error("Không tìm thấy seriesMasterId");
      return;
    }

    try {
      console.log(selectedEvent);
      // 1) call BE để lấy master event
      const { data: masterEvent } = await axios.get(
        `/api/meeting-room-booking/${masterId}`,
        {
          params: { roomEmail: selectedEvent.extendedProps.room }
        }
      );

      // 2) Prefill formData
      setFormData({
        start: new Date(masterEvent.start.dateTime),
        end: new Date(masterEvent.end.dateTime),
        subject: masterEvent.subject
      });

      // 3) Prefill phòng: tìm trong attendees.type="resource", fallback location
      const resource =
        masterEvent.attendees?.find((a: any) => a.type === "resource")
          ?.emailAddress?.address ||
        masterEvent.location?.emailAddress?.address ||
        masterEvent.location?.displayName ||
        "";
      setBookingRoom(resource);

      // 4) Prefill attendees (nhân sự)
      const humans = (masterEvent.attendees || [])
        .filter((a: any) => a.type === "required")
        .map((a: any) => a.emailAddress.address);
      setAttendees(humans);

      // 5) Prefill allDay
      setAllDay(masterEvent.isAllDay || false);

      // 6) Prefill recurrence nếu có
      if (masterEvent.recurrence) {
        const { pattern, range } = masterEvent.recurrence;
        setIsRecurring(true);
        setRecurrence({
          frequency: pattern.type.toLowerCase() as any,
          interval: pattern.interval,
          daysOfWeek: (pattern.daysOfWeek || []).map((d: string) => d.toLowerCase()),
          endDate: range.endDate
        });
      } else {
        setIsRecurring(false);
        setRecurrence(defaultRecurrence);
      }

      // 7) Mở form Edit và đóng Detail
      setOpenDetails(false);
      setOpenBookingForm(true);
    } catch (err) {
      console.error("Fetch master event failed", err);
      toast.error("Không thể lấy dữ liệu chuỗi để sửa");
    }
  };

  useEffect(() => {
    if (roomQuery.trim()) {
      setRoomSuggestions(
        allRooms.filter(r =>
          r.toLowerCase().includes(roomQuery.toLowerCase())
        )
      );
    } else {
      setRoomSuggestions([]);
    }
  }, [roomQuery, allRooms]);

  // 2) Khi selectedDate thay đổi, điều hướng lịch về ngày đó
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    api?.gotoDate(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    // Initialize the Teams SDK context
    app.initialize().then(() => {
      app.getContext().then((context) => {
        const email = context?.user?.userPrincipalName;
        setUserEmail(email || null);
      }).catch((error) => {
        console.error("Failed to get context:", error);
      });
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const isoDate = DateTime.fromJSDate(selectedDate).toISODate();
        const res = await axios.get("/api/room-schedule", {
          params: { rooms: selectedRooms, mode: "day", date: isoDate }
        });
        setSchedule(res.data);
      } catch (err) {
        console.error("Error fetching mobile schedule:", err);
      }
    })();
  }, [selectedRooms, selectedDate, isRefetch]);
  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, []);
  useEffect(() => {
    // sau khi mount, báo cho FC đo lại
    const api = calendarRef.current?.getApi();
    api?.updateSize();
    const nowStr = DateTime.local().setZone("Asia/Ho_Chi_Minh").toFormat("HH:mm:ss");
    api?.scrollToTime(nowStr);
  }, []);

  const handleCreateBooking = async (start: Date, end: Date) => {
    if (!bookingRoom || !formData.subject) return;

    const fakeIdBase = `temp-${Date.now()}`;

    // Hàm sinh danh sách JS Date cho các occurrence
    // Giả sử `recurrence.endType === "endDate"` LUÔN luôn true,
    // và `recurrence.endDate` không bao giờ null

    const generateOccurrences = (): { start: Date; end: Date }[] => {
      const dates: { start: Date; end: Date }[] = [];
      const { frequency, interval, daysOfWeek, endDate: recEnd } = recurrence;

      // Nếu chưa có endDate thì trả về rỗng
      if (!recEnd) return dates;

      // 1) Parse start và endDate
      const startDT = DateTime.fromJSDate(start)
        .setZone("Asia/Ho_Chi_Minh");
      const rawEnd = typeof recEnd === "string"
        ? DateTime.fromISO(recEnd)
        : DateTime.fromJSDate(recEnd);

      // 2) Align finalDate về cùng time-of-day với start
      const finalDate = rawEnd
        .setZone("Asia/Ho_Chi_Minh")
        .set({
          hour: startDT.hour,
          minute: startDT.minute,
          second: startDT.second,
          millisecond: startDT.millisecond
        });

      // 3) Hàm thêm occurrence vào mảng
      const addOccurrence = (dt: DateTime) => {
        dates.push({
          start: dt.toJSDate(),
          end: DateTime.fromJSDate(end)
            .setZone("Asia/Ho_Chi_Minh")
            .set({ year: dt.year, month: dt.month, day: dt.day })
            .toJSDate()
        });
      };

      // 4) Sinh theo frequency
      if (frequency === "daily") {
        let cursor = startDT;
        while (cursor <= finalDate) {
          addOccurrence(cursor);
          cursor = cursor.plus({ days: interval });
        }

      } else if (frequency === "weekly") {
        // map ["monday",...] → [1,2,...]
        const dowMap = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
        const dowNums = daysOfWeek.map(d => dowMap.indexOf(d) + 1);

        // Khởi tuần đầu
        let weekStart = startDT.startOf("week");
        // Duyệt tuần tới khi vẫn ≤ tuần chứa finalDate
        while (weekStart.startOf("week") <= finalDate.startOf("week")) {
          for (const wd of dowNums) {
            const dt = weekStart.set({ weekday: wd, hour: startDT.hour, minute: startDT.minute });
            if (dt >= startDT && dt <= finalDate) {
              addOccurrence(dt);
            }
          }
          weekStart = weekStart.plus({ weeks: interval });
        }

      } else if (frequency === "monthly") {
        let cursor = startDT;
        while (cursor <= finalDate) {
          addOccurrence(cursor);
          cursor = cursor.plus({ months: interval });
        }
      }

      return dates;
    };

    // Tạo dummy events
    const dummyEvents = isRecurring
      ? generateOccurrences().map((occ, idx) => {
        const dayFmt = occ.start
          ? DateTime.fromJSDate(occ.start)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("cccc, dd/MM/yyyy")
          : "";
        const startFmt = allDay
          ? "All day"
          : DateTime.fromJSDate(occ.start)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("HH:mm");
        const endFmt = allDay
          ? ""
          : DateTime.fromJSDate(occ.end)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("HH:mm");

        return {
          id: `${fakeIdBase}-${idx}`,
          subject: formData.subject,
          room: bookingRoom,
          organizer: userEmail,
          attendees,
          allDay,
          day: dayFmt,
          start: startFmt,
          end: endFmt
        };
      })
      : [
        {
          id: fakeIdBase,
          subject: formData.subject,
          room: bookingRoom,
          organizer: userEmail,
          attendees,
          allDay,
          day: DateTime.fromJSDate(start)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("cccc, dd/MM/yyyy"),
          start: allDay
            ? "All day"
            : DateTime.fromJSDate(start)
              .setZone("Asia/Ho_Chi_Minh")
              .toFormat("HH:mm"),
          end: allDay
            ? ""
            : DateTime.fromJSDate(end)
              .setZone("Asia/Ho_Chi_Minh")
              .toFormat("HH:mm")
        }
      ];

    // Optimistic UI: thêm tất cả dummy events
    setSchedule(prev => [...prev, ...dummyEvents]);

    // build payload như cũ...
    const payload: any = { organizerEmail: userEmail, roomEmail: bookingRoom, attendees, allDay, subject: formData.subject };
    if (allDay) {
      const s = DateTime.fromJSDate(start).setZone("UTC").startOf("day").toISO();
      const e = DateTime.fromJSDate(end).setZone("UTC").plus({ days: 1 }).startOf("day").toISO();
      payload.start = s; payload.end = e;
    } else {
      payload.start = start.toISOString(); payload.end = end.toISOString();
    }
    if (isRecurring) {
      payload.recurrence = {
        pattern: {
          type: recurrence.frequency.toUpperCase(),
          interval: recurrence.interval,
          ...(recurrence.frequency === "weekly" && { daysOfWeek: recurrence.daysOfWeek.map(d => d[0].toUpperCase() + d.slice(1)) })
        },
        range: {
          type: "endDate",
          startDate: DateTime.fromJSDate(formData.start).toISODate(),
          endDate: recurrence.endDate
        }
      };
    }

    try {
      const res = await axios.post("/api/meeting-room-booking", payload);
      if (res.status === 200) {
        toast.success("Done");
        setTimeout(() => setIsRefetch(prev => prev + 1), 4000);
      } else {
        throw new Error();
      }
    } catch (error) {
      // rollback all dummy events
      setSchedule(prev => prev.filter(ev => !ev.id.startsWith(fakeIdBase)));
      toast.error("Fail to book meeting room");
    } finally {
      setOpenBookingForm(false);
      setFormData({ start: new Date(), end: new Date(), subject: "" });
      setAttendees([]);
    }
  };
  const handleAddAttendee = (email: string) => {
    setAttendees((prevAttendees) => [...prevAttendees, email]);
    setSearchQuery("");
    setUserSuggestions([]);
  };
  const removeAttendee = (email: string) =>
    setAttendees(prev => prev.filter(a => a !== email));
  const getUsers = async (searchQuery: string) => {
    try {
      const response = await axios.get("/api/search-users", {
        params: { query: searchQuery }
      });
      setUserSuggestions(response.data); // Cập nhật gợi ý người dùng
    } catch (error) {
      console.error("Error fetching users from API:", error);
    }
  };
  const displayMobileSchedule = useMemo(() => {
    return schedule.filter(item => {
      // ① room filter (as you had before)
      if (selectedRooms.length > 0 && !selectedRooms.includes(item.room)) {
        return false;
      }
      // ② if “My Events” is ON, only keep organizer==me or I’m in attendees
      if (showMyEvents) {
        return (
          item.organizer === userEmail ||
          (item.attendees as string[]).includes(userEmail!)
        );
      }
      return true;
    });
  }, [schedule, selectedRooms, showMyEvents, userEmail]);

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.currentTarget.value;
    setSearchQuery(query);

    if (query.length >= 3) { // Khi người dùng nhập ít nhất 3 ký tự
      await getUsers(query); // Gọi API để lấy người dùng
    } else {
      setUserSuggestions([]); // Nếu nhập ít hơn 3 ký tự, không hiển thị gợi ý
    }
  };
  const handleUpdateBooking = async (eventId: string) => {
    if (!selectedEvent) return;

    // Build payload từ formData, bookingRoom, attendees, allDay, recurrence…
    const payload: any = {
      organizerEmail: userEmail,
      roomEmail: bookingRoom,
      subject: formData.subject,
      attendees,
      allDay
    };
    payload.start = allDay
      ? DateTime.fromJSDate(formData.start).setZone("UTC").startOf("day").toISO()
      : formData.start.toISOString();
    payload.end = allDay
      ? DateTime.fromJSDate(formData.end).setZone("UTC").plus({ days: 1 }).startOf("day").toISO()
      : formData.end.toISOString();

    if (isRecurring) {
      payload.recurrence = {
        pattern: {
          type: recurrence.frequency.toUpperCase(),
          interval: recurrence.interval,
          ...(recurrence.frequency === "weekly" && {
            daysOfWeek: recurrence.daysOfWeek.map(d =>
              d[0].toUpperCase() + d.slice(1)
            )
          })
        },
        range: {
          type: "endDate",
          startDate: DateTime.fromJSDate(formData.start).toISODate(),
          endDate: recurrence.endDate
        }
      };
    }

    try {
      const res = await axios.patch(
        `/api/meeting-room-booking/${encodeURIComponent(eventId)}`,
        payload
      );
      if (res.status === 200) {
        toast.success("Cập nhật thành công!");

        // ② Update trực tiếp trong schedule state
        setSchedule(prev =>
          prev.map(item => {
            if (item.id !== eventId) return item;

            // convert lại để hiển thị giống mobile list
            const dayFmt = DateTime.fromJSDate(formData.start)
              .setZone("Asia/Ho_Chi_Minh")
              .toFormat("cccc, dd/MM/yyyy");
            const startFmt = allDay
              ? "All day"
              : DateTime.fromJSDate(formData.start)
                .setZone("Asia/Ho_Chi_Minh")
                .toFormat("HH:mm");
            const endFmt = allDay
              ? ""
              : DateTime.fromJSDate(formData.end)
                .setZone("Asia/Ho_Chi_Minh")
                .toFormat("HH:mm");

            return {
              ...item,
              subject: formData.subject,
              room: bookingRoom,
              attendees,
              day: dayFmt,
              start: startFmt,
              end: endFmt,
              allDay
            };
          })
        );

        // ③ Đóng modal, clear selection và reset form
        setOpenBookingForm(false);
        setOpenDetails(false);
        setSelectedEvent(null);
        setFormData({ start: new Date(), end: new Date(), subject: "" });
        setBookingRoom("");
        setAttendees([]);
        setIsRecurring(false);
        setRecurrence(defaultRecurrence);
      } else {
        throw new Error(`Status ${res.status}`);
      }
    } catch (err) {
      console.error("Error updating booking:", err);
      toast.error("Cập nhật thất bại");
    }
  };
  useEffect(() => {
    console.log("Recurrence changed:", recurrence);
  }, [recurrence]);

  const handleEventClick = (clickInfo: EventClickArg) => {
    clickInfo.jsEvent.preventDefault();
    setSelectedEvent(clickInfo.event);
    setOpenDetails(true);
  };
  const handlePrev = () => {
    const api = calendarRef.current?.getApi();
    api?.prev();
    setSelectedDate(api?.getDate() || selectedDate);
  };

  const handleNext = () => {
    const api = calendarRef.current?.getApi();
    api?.next();
    setSelectedDate(api?.getDate() || selectedDate);
  };

  const handleRoomSelection = (room: string) => {
    setSelectedRooms(prev =>
      prev.includes(room) ? prev.filter(r => r !== room) : [...prev, room]
    );
  };
  const handleDateClick = (info: DateClickArg) => {
    if (info.date < new Date()) {
      toast.error("Không thể tạo lịch trong quá khứ");
      return;
    }

    const start = info.date;
    const end = DateTime.fromJSDate(start)
      .plus({ hours: 1 })
      .toJSDate();

    setFormData({ start, end, subject: "" });
    setBookingRoom(selectedRooms.length === 1 ? selectedRooms[0] : "");
    setOpenBookingForm(true);
  };
  // trong component RoomScheduleMobile
  const handleMobileEventDrop = async (info: EventDropArg) => {
    const ev = info.event;
    // chỉ cho phép update nếu là event do mình tạo
    if (ev.extendedProps.organizer !== userEmail) {
      info.revert();
      return;
    }

    // build payload giống handleUpdateBooking
    const payload: any = {
      organizerEmail: userEmail,
      roomEmail: ev.extendedProps.room,
      subject: ev.title,
      attendees: ev.extendedProps.participants || [],
      allDay: ev.allDay
    };
    // convert start/end sang ISO với timezone UTC
    payload.start = DateTime.fromJSDate(ev.start!).toUTC().toISO();
    payload.end = DateTime.fromJSDate(ev.end!).toUTC().toISO();

    try {
      await axios.patch(
        `/api/meeting-room-booking/${encodeURIComponent(ev.id)}`,
        payload
      );
      toast.success("Cập nhật thời gian thành công");
    } catch (err) {
      toast.error("Cập nhật thất bại");
      info.revert();
    }
  };
  const handleMobileEventResize = async (info: EventResizeDoneArg) => {
    // EventResizeDoneArg cũng có info.event, info.revert()
    await handleMobileEventDrop(info as unknown as EventDropArg);
  };
  const touchStartX = useRef(0);

  // 1) Touch handlers để detect swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const threshold = 100; // px
    if (dx > threshold) {
      handlePrev();
    } else if (dx < -threshold) {
      handleNext();
    }
  };

  // 2) Handler Today
  const handleToday = () => {
    const today = new Date();
    setSelectedDate(today);
    calendarRef.current?.getApi().gotoDate(today);
  };

  return (
    <div className="mobile-calendar-container">
      <input
        type="date"
        ref={dateInputRef}
        style={{ position: "absolute", width: 0, height: 0, opacity: 0 }}
        onChange={e => {
          if (e.target.value) {
            setSelectedDate(new Date(e.target.value));
          }
        }}
      />
      <div className="mobile-month-header">
        <button onClick={handlePrev} aria-label="Prev day">‹</button>
        <button className="today-button" onClick={handleToday} aria-label="Today">Today</button>
        <div className="title" style={{ cursor: "pointer" }}
          onClick={() => dateInputRef.current?.click()} >

          {DateTime.fromJSDate(selectedDate).setLocale("vi").toFormat("dd/LL/yyyy")}
        </div>
        <button onClick={handleNext} aria-label="Next day">›</button>

      </div>
      <Flex padding="padding.medium" vAlign="center">
        <Checkbox
          label={
            <Text
              content="My Event"
              styles={{ root: { color: "#fff" } }}
            />
          }
          checked={showMyEvents}
          onChange={(_, data) => setShowMyEvents(!!data?.checked)}
        />
      </Flex>
      <div className="mobile-room-select">
        {Object.entries(roomColors).map(([email, color]) => {
          const name = email.split("@")[0];
          const sel = selectedRooms.includes(email);
          return (
            <label key={email}>
              <input
                type="checkbox"
                checked={sel}
                onChange={() => handleRoomSelection(email)}
              />
              <span
                style={{ backgroundColor: sel ? color : "transparent", borderColor: color }}
              />
              {name}
            </label>
          );
        })}
      </div>

      <div className="mobile-calendar-content">
        <div
          className="mobile-calendar-content"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[timeGridPlugin, interactionPlugin]}
            scrollTime="09:00:00"
            initialView="timeGridDay"
            headerToolbar={false}
            slotMinWidth={80}
            events={convertScheduleToEvents(displayMobileSchedule).filter(
              ev => selectedRooms.length === 0 || selectedRooms.includes(ev.room)
            )}
            height="100vh"
            contentHeight="auto"
            longPressDelay={1000}
            nowIndicator={true}
            editable={true}
            eventStartEditable={true}
            eventDurationEditable={true}
            dateClick={handleDateClick} // ← fire on single tap
            selectable={true}
            select={(info) => {
              if (selectedEvent) {

                setOpenDetails(false);
                setSelectedEvent(null); // Reset selectedEvent
              }
              setFormData({ start: info.start, end: info.end, subject: "" }); // Clear subject when selecting a time
              setBookingRoom("");
              setOpenBookingForm(true);
            }}
            eventAllow={(dropInfo, draggedEvent) => {

              return draggedEvent?.extendedProps.organizer === userEmail &&
                dropInfo.start! >= new Date() &&
                dropInfo.end! > dropInfo.start!;
            }}
            selectAllow={(selectInfo) => {
              return selectInfo.start >= new Date();
            }}
            eventDrop={handleMobileEventDrop}
            eventResize={handleMobileEventResize}
            eventClick={handleEventClick}
            eventDidMount={info => {
              const el = info.el as HTMLElement;
              el.style.position = "relative";

              const bar = document.createElement("div");
              Object.assign(bar.style, {
                position: "absolute",
                left: "2px",
                top: "2px",
                bottom: "2px",
                width: "4px",
                backgroundColor: info.event.borderColor as string,
                borderRadius: "2px"
              });
              el.appendChild(bar);
            }}
          />
          {/* === Modal Tạo / Cập nhật === */}
          {openBookingForm && (
            <Dialog
              open
              header={selectedEvent ? "Cập nhật sự kiện" : "Tạo sự kiện mới"}
              styles={{ dialog: { maxWidth: "90vw", padding: "1rem" } }}
              onCancel={() => setOpenBookingForm(false)}
              content={
                <Form
                  onSubmit={() =>
                    selectedEvent
                      ? handleUpdateBooking(selectedEvent.id)
                      : handleCreateBooking(formData.start, formData.end)
                  }
                >
                  <Flex column gap="gap.medium">
                    {/* — Tiêu đề — */}
                    <FormField label="Tiêu đề" required>
                      <Input
                        placeholder="Nhập tiêu đề"
                        value={formData.subject}
                        onChange={(_, data) =>
                          setFormData({ ...formData, subject: data?.value ?? "" })
                        }
                        fluid
                      />
                    </FormField>

                    {/* — Ngày & giờ — */}
                    <FormField label="Ngày & giờ">
                      <Flex wrap gap="gap.small">
                        <Input
                          type="date"
                          value={DateTime.fromJSDate(formData.start).toISODate()}
                          onChange={(_, data) => {
                            const d = DateTime.fromISO(data?.value ?? "").toJSDate();
                            setFormData({ start: d, end: d, subject: formData.subject });
                          }}
                          fluid
                        />
                        {!allDay && (
                          <>
                            <Input
                              type="time"
                              value={DateTime.fromJSDate(formData.start).toFormat("HH:mm")}
                              onChange={(_, data) => {
                                const [h, m] = (data?.value as string)
                                  .split(":")
                                  .map(Number);
                                setFormData(prev => ({
                                  ...prev,
                                  start: DateTime.fromJSDate(prev.start)
                                    .set({ hour: h, minute: m })
                                    .toJSDate()
                                }));
                              }}
                              fluid
                            />
                            <Input
                              type="time"
                              value={DateTime.fromJSDate(formData.end).toFormat("HH:mm")}
                              onChange={(_, data) => {
                                const [h, m] = (data?.value as string)
                                  .split(":")
                                  .map(Number);
                                setFormData(prev => ({
                                  ...prev,
                                  end: DateTime.fromJSDate(prev.end)
                                    .set({ hour: h, minute: m })
                                    .toJSDate()
                                }));
                              }}
                              fluid
                            />
                          </>
                        )}
                        <Checkbox
                          label="Cả ngày"
                          checked={allDay}
                          onChange={(_, data) => setAllDay(data?.checked!)}
                        />
                      </Flex>
                    </FormField>

                    {/* — Người tham dự — */}
                    <Dropdown<any>
                      multiple
                      search
                      fluid
                      placeholder="Nhập email, Enter để thêm"
                      noResultsMessage="Nhập ít nhất 3 ký tự"
                      items={userSuggestions.map(u => ({
                        header: u.displayName,
                        value: u.mail
                      }))}
                      value={attendees}
                      onSearchQueryChange={(_, data) => {
                        const q = data?.searchQuery ?? "";
                        if (q.length >= 3) getUsers(q);
                      }}
                      onChange={(_, data) => {
                        const raw = data?.value ?? [];
                        const arr = Array.isArray(raw) ? raw : [raw];
                        const emails = arr.map(item =>
                          typeof item === "string" ? item : (item as any).value
                        );
                        setAttendees(emails);
                      }}
                    />

                    {/* — Phòng — */}
                    <FormField label="Phòng" required>
                      <Dropdown
                        placeholder="Chọn phòng"
                        fluid
                        items={Object.keys(roomColors).map(email => ({
                          header: email.split("@")[0],
                          value: email
                        }))}
                        value={bookingRoom}
                        onChange={(_, data) => {
                          const raw = data?.value;
                          const room = typeof raw === "string" ? raw : (raw as any).value;
                          setBookingRoom(room);
                        }}
                      />
                    </FormField>

                    {/* — Định kỳ — */}
                    <FormField label="Định kỳ">
                      <Button
                        text
                        content={isRecurring ? "Bỏ định kỳ" : "🔁Đặt là định kỳ"}
                        onClick={() => setIsRecurring(!isRecurring)}
                      />
                      {isRecurring && (
                        <div className="recurrence-form">
                          {/* 1) Interval & Frequency */}
                          <Flex gap="gap.small" vAlign="center">
                            <Text>Lặp mỗi</Text>
                            <Input
                              type="number"
                              min={1}
                              value={recurrence.interval}
                              onChange={(_, data) =>
                                setRecurrence(prev => ({
                                  ...prev,
                                  interval: Number(data?.value ?? 1)
                                }))
                              }
                              style={{ width: 60 }}
                            />
                            <Dropdown<any>
                              items={[
                                { header: "Ngày", value: "daily" },
                                { header: "Tuần", value: "weekly" },
                                { header: "Tháng", value: "monthly" }
                              ]}
                              value={recurrence.frequency}
                              onChange={(_, data) => {
                                const raw = data?.value;
                                const freq =
                                  typeof raw === "object" && raw !== null && "value" in raw
                                    ? (raw as any).value
                                    : raw;
                                setRecurrence(prev => ({
                                  ...prev,
                                  frequency: freq as any,
                                  daysOfWeek: freq === "weekly" ? [] : prev.daysOfWeek
                                }));
                              }}
                            />
                          </Flex>

                          {/* 2) Chọn thứ nếu weekly */}
                          {recurrence.frequency === "weekly" && (
                            <div className="weekdays-row">
                              {[
                                "monday",
                                "tuesday",
                                "wednesday",
                                "thursday",
                                "friday",
                                "saturday",
                                "sunday"
                              ].map((day, i) => (
                                <button
                                  key={day}
                                  type="button"
                                  className={
                                    recurrence.daysOfWeek.includes(day)
                                      ? "day active"
                                      : "day"
                                  }
                                  onClick={() =>
                                    setRecurrence(prev => ({
                                      ...prev,
                                      daysOfWeek: prev.daysOfWeek.includes(day)
                                        ? prev.daysOfWeek.filter(d => d !== day)
                                        : [...prev.daysOfWeek, day]
                                    }))
                                  }
                                >
                                  {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"][i]}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* 3) Ngày kết thúc */}
                          <Flex gap="gap.small" vAlign="center">
                            <Text>Kết thúc:</Text>
                            <FormField label="Kết thúc (ngày cụ thể)" required>
                              <Input
                                type="date"
                                min={DateTime.fromJSDate(formData.start).toISODate()}
                                value={recurrence.endDate}
                                onChange={(_, data) =>
                                  setRecurrence(prev => ({
                                    ...prev,
                                    endDate: data?.value ?? ""
                                  }))
                                }
                                fluid
                              />
                            </FormField>
                          </Flex>
                        </div>
                      )}
                    </FormField>

                    {/* — Nút Hủy / Lưu — */}
                    <Flex gap="gap.small" hAlign="end" styles={{ paddingTop: "1rem" }}>
                      <Button content="Hủy" onClick={() => setOpenBookingForm(false)} />
                      <Button primary content="Lưu" type="submit" />
                    </Flex>
                  </Flex>
                </Form>
              }
              footer={null}
            />
          )}

          {openDetails && selectedEvent && (
            <Dialog
              open
              header="Chi tiết sự kiện"
              styles={{ dialog: { maxWidth: "90vw" } }}
              onCancel={() => setOpenDetails(false)}
              content={
                <Flex column gap="gap.small">
                  <Flex space="between">
                    <Text weight="semibold">Tiêu đề:</Text>
                    <Text>{selectedEvent.title}</Text>
                  </Flex>
                  <Flex space="between">
                    <Text weight="semibold">Bắt đầu:</Text>
                    <Text>
                      {selectedEvent.start
                        ?.toLocaleString()}
                    </Text>
                  </Flex>
                  {selectedEvent.end && (
                    <Flex space="between">
                      <Text weight="semibold">Kết thúc:</Text>
                      <Text>
                        {selectedEvent.end.toLocaleString()}
                      </Text>
                    </Flex>
                  )}
                  <Flex space="between">
                    <Text weight="semibold">Địa điểm:</Text>
                    <Text>
                      {selectedEvent.extendedProps.room.split("@")[0] + " meeting room"}
                    </Text>
                  </Flex>
                  <Text weight="semibold">Người tham dự:</Text>
                  {selectedEvent.extendedProps.participants?.length
                    ? (
                      <List
                        items={selectedEvent.extendedProps.participants.map((p: string) => ({
                          key: p,
                          header: p
                        }))}
                        styles={{ list: { maxHeight: "8rem", overflowY: "auto" } }}
                      />
                    )
                    : (
                      <Text styles={{ root: { fontStyle: "italic" } }}>
                        Chưa có
                      </Text>
                    )}
                </Flex>
              }
              footer={
                <Flex gap="gap.small" hAlign="end">
                  <Button
                    content="Đóng"
                    onClick={() => setOpenDetails(false)}
                  />

                  {selectedEvent.extendedProps.organizer === userEmail && (
                    <>
                      {selectedEvent.extendedProps.seriesMasterId
                        ? (
                          <>
                            <Button
                              content="Sửa"
                              primary
                              onClick={handleEditOccurrence}
                            />
                            {/* <Button
                            content="Sửa chuỗi"
                            primary
                            onClick={handleEditSeries}
                          /> */}
                          </>
                        )
                        : (
                          <Button
                            content="Sửa"
                            primary
                            onClick={handleEditOccurrence}
                          />
                        )}

                      <Button
                        content="Xóa"
                        iconPosition="before"
                        styles={{ root: { color: "#d32f2f" } }}
                        onClick={() => setShowDeleteConfirmMobile(true)}
                      />
                      {showDeleteConfirmMobile && (
                        <Dialog
                          open
                          header="Xác nhận xóa"
                          content={
                            selectedEvent.extendedProps.recurrence
                              ? "Sự kiện này là định kỳ. Bạn muốn xóa thế nào?"
                              : "Bạn có chắc muốn xóa sự kiện này không?"
                          }
                          footer={
                            <Flex gap="gap.small" hAlign="end">
                              {selectedEvent.extendedProps.recurrence && (
                                <Button
                                  content="Chỉ xóa lần này"
                                  primary
                                  onClick={() => handleDeleteMobileEvent(false)}
                                />
                              )}
                              {selectedEvent.extendedProps.recurrence && (
                                <Button
                                  content="Xóa toàn bộ chuỗi"
                                  styles={{ root: { color: "#d32f2f" } }}
                                  onClick={() => handleDeleteMobileEvent(true)}
                                />
                              )}
                              <Button
                                content="Hủy"
                                onClick={() => setShowDeleteConfirmMobile(false)}
                              />
                              {!selectedEvent.extendedProps.recurrence && (
                                <Button
                                  content="Xóa"
                                  primary
                                  onClick={() => handleDeleteMobileEvent(false)}
                                />
                              )}
                            </Flex>
                          }
                        />
                      )}
                    </>
                  )}
                </Flex>
              }
            />
          )}

        </div>
      </div>
    </div>
  );
}
